import { useReactiveVar } from "@apollo/client";
import { useCallback, useState } from "react";
import { LINE_SHARE_ORIGIN, HASH_TAG_ENCODE, TWITTER_SHARE_ORIGIN } from "./const";
import { getDetailUrl } from "~/components/blocks/product/util/get-detail-url";
import type { DownloadModalData } from "~/components/stores/detail-product/store";
import {
  clickedContentIdVar,
  itemVariantVar,
  downloadModalDataVar,
  isOpenDownloadModalVar,
} from "~/components/stores/detail-product/store";
import { openTextOnlyToast } from "~/components/stores/toast/store";
import { runningInBrowser } from "~/const/browser";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery, G_FetchViewerVideoBookmarksQuery } from "~/lib/graphql";
import { useAddVideoBookmarkMutation, useDeleteVideoBookmarksMutation, GetVideoDocument } from "~/lib/graphql";
import { TRACKING_PRICE } from "~/lib/tracking/const";
import { sendTrackingAddToWishlist } from "~/lib/tracking/events";
import { getDevice, getPlayDevice } from "~/util/get-device";
import { uriConverter } from "~/util/uri-converter";

type ShareIconList = {
  /** Iconコンポーネントのname属性に渡す文字列 */
  icon: "colorLine" | "colorTwitter" | "copyLink";
  /** アイコンボタンの下に表示するボタンの概要 */
  name: "LINE" | "Twitter" | "URLコピー";
  /** アイコン押下時に発火するイベントハンドラ */
  onIconClicked: (titleName: string) => void;
};

type UseDesktopActionButtonArgs = { videoData: G_GetVideoQuery["video"] };

// TODO: useDesktopActionButtonとuseMobileActionButtonを統合
export const useDesktopActionButton = ({ videoData }: UseDesktopActionButtonArgs) => {
  const [addVideoBookmarkExec, { loading: isLoadingAddVideoBookmark }] = useAddVideoBookmarkMutation();
  const [deleteVideoBookmarkExec, { loading: isLoadingDeleteVideoBookmark }] = useDeleteVideoBookmarksMutation();

  const itemVariant = useReactiveVar(itemVariantVar);

  const { season, content, spot } = useQueryString();

  const [isFavoriteRegistered, setIsFavoriteRegistered] = useState(videoData.hasBookmark);
  const [isOpenTooltip, setIsOpenTooltip] = useState(false);
  const tooltipToggleHandler = useCallback(() => setIsOpenTooltip((prev) => !prev), []);
  const onTooltipClose = useCallback(() => setIsOpenTooltip(false), []);

  const deleteBookmarkHandler = async () => {
    if (isLoadingDeleteVideoBookmark) return;

    const { data } = await deleteVideoBookmarkExec({
      variables: {
        input: {
          seasonIds: [season],
        },
      },
      update: (cache, { data }) => {
        if (data?.deleteVideoBookmarks.mutationId) {
          // memo: お気に入り一覧のcacheを更新
          cache.modify({
            fields: {
              viewer(existing: G_FetchViewerVideoBookmarksQuery["viewer"]) {
                // eslint-disable-next-line unused-imports/no-unused-vars
                const { videoBookmarks, ...rest } = existing;
                return {
                  ...rest,
                } as G_FetchViewerVideoBookmarksQuery["viewer"];
              },
            },
          });

          // memo: 作品末端自身のcacheを更新
          cache.writeQuery({
            query: GetVideoDocument,
            variables: {
              seasonId: season,
              contentId: content ?? "",
              device: getDevice(),
              playDevice: getPlayDevice(),
              isLoggedIn: true,
              isContentId: content !== undefined,
            },
            data: {
              video: {
                ...videoData,
                hasBookmark: false,
              },
            },
          });

          // memo: 到達不可能なキャッシュを削除
          cache.gc();
        }
      },
    });

    if (!data) return;

    if (data.deleteVideoBookmarks.mutationId) {
      setIsFavoriteRegistered(false);
      openTextOnlyToast("DELETE_BOOKMARK");
    } else {
      openTextOnlyToast("FAILED_DELETE_BOOKMARK");
    }
  };

  const addBookmarkHandler = async () => {
    if (isLoadingAddVideoBookmark) return;

    const { data } = await addVideoBookmarkExec({
      variables: {
        input: {
          seasonId: season,
        },
      },
      update: (cache, { data }) => {
        if (data?.addVideoBookmark.mutationId) {
          // memo:お気に入り一覧のcacheを更新
          cache.modify({
            fields: {
              viewer(existing: G_FetchViewerVideoBookmarksQuery["viewer"]) {
                // eslint-disable-next-line unused-imports/no-unused-vars
                const { videoBookmarks, ...rest } = existing;
                return {
                  ...rest,
                } as G_FetchViewerVideoBookmarksQuery["viewer"];
              },
            },
          });
          // memo:作品末端自身のcacheを更新
          cache.writeQuery({
            query: GetVideoDocument,
            variables: {
              seasonId: season,
              contentId: content ?? "",
              device: getDevice(),
              playDevice: getPlayDevice(),
              isLoggedIn: true,
              isContentId: content !== undefined,
            },
            data: {
              video: {
                ...videoData,
                hasBookmark: true,
              },
            },
          });
          // memo: 到達不可能なキャッシュを削除
          cache.gc();
        }
      },
    });

    if (!data) return;

    if (data.addVideoBookmark.mutationId) {
      setIsFavoriteRegistered(true);
      openTextOnlyToast("ADD_BOOKMARK");

      sendTrackingAddToWishlist({
        seasonId: season,
        item_name: videoData.titleName,
        priceInfo: { price: TRACKING_PRICE },
        itemVariant,
      });
    } else {
      openTextOnlyToast("FAILED_ADD_BOOKMARK");
    }
  };

  const onDownloadBtnClicked = (contentId: string, downloadModalData: DownloadModalData) => {
    isOpenDownloadModalVar(true);
    clickedContentIdVar(contentId);
    downloadModalDataVar(downloadModalData);
  };

  /** MEMO : http環境では使用できない */
  const copyUrlHandler = async () => {
    if (!runningInBrowser) return;

    const { ogpDetailUrl } = getDetailUrl({
      spot,
      videoData,
    });

    try {
      await navigator.clipboard.writeText(ogpDetailUrl);
      setIsOpenTooltip(false);
      openTextOnlyToast("COPY_URL");
    } catch (error) {
      openTextOnlyToast("FAILED_COPY_URL");
    }
  };

  const shareIconList: ShareIconList[] = [
    {
      icon: "colorLine",
      name: "LINE",
      onIconClicked: (titleName: string) => {
        const { aiadDetailBackUrl } = getDetailUrl({
          spot,
          videoData,
        });

        const lineAiAdMeasureUrl = `https://rcv.ixd.dmm.com/api/surl?urid=wdT6LSW1&lurl=${aiadDetailBackUrl}`;

        // MEMO（kimura）: 半角の「#」を全角の「＃」に変換する。理由は半角の「#」の場合ハッシュタグとして認識されるから
        const replaceTitleName = titleName.replaceAll("#", "＃");

        const lineShareUrl = `${LINE_SHARE_ORIGIN}?url=${uriConverter.encode(
          lineAiAdMeasureUrl
        )}&text=「${replaceTitleName}」${HASH_TAG_ENCODE}DMMTV で配信中！`;

        window.open(lineShareUrl, "_blank", "noreferrer");
      },
    },
    {
      icon: "colorTwitter",
      name: "Twitter",
      onIconClicked: (titleName: string) => {
        const { aiadDetailBackUrl } = getDetailUrl({
          spot,
          videoData,
        });

        const twitterMeasureUrl = `https://rcv.ixd.dmm.com/api/surl?urid=cSHqstvj&lurl=${aiadDetailBackUrl}`;

        // MEMO（kimura）: 半角の「#」を全角の「＃」に変換する。理由は半角の「#」の場合ハッシュタグとして認識されるから
        const replaceTitleName = titleName.replaceAll("#", "＃");

        const twitterShareUrl = `${TWITTER_SHARE_ORIGIN}?url=${uriConverter.encode(
          twitterMeasureUrl
        )}&text=「${replaceTitleName}」${HASH_TAG_ENCODE}DMMTV で配信中！`;

        window.open(twitterShareUrl, "_blank", "noreferrer");
      },
    },
    {
      icon: "copyLink",
      name: "URLコピー",
      onIconClicked: copyUrlHandler,
    },
  ];

  return {
    shareIconList,
    isFavoriteRegistered,
    isOpenTooltip,
    addBookmarkHandler,
    deleteBookmarkHandler,
    onDownloadBtnClicked,
    onTooltipClose,
    tooltipToggleHandler,
  };
};
